import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";
import AutoSizer from "react-virtualized-auto-sizer";
import { ListOnItemsRenderedProps, ListOnScrollProps, VariableSizeList } from "react-window";

import Note from "@mapmycustomers/shared/types/entity/common/Note";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";

import noNotes from "@app/assets/list/preview/no-notes.svg";
import { getAllTeamsOfEntireOrganization, getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";
import useModalVisibilityAnalyticEffect from "@app/util/hook/useModalVisibilityAnalyticEffect";

import AddOrCreateButton from "../AddOrCreateButton";
import NoData from "../NoData";
import TabLayout from "../TabLayout";

import EditNoteForm from "./EditNoteForm";
import ListItem, { ListData } from "./ListItem";
import styles from "./Notes.module.scss";

const DEFAULT_ITEM_SIZE = 60; // px, rough estimation of item height, to help distributing them vertically unless real size is calculated
const ESTIMATED_ITEM_SIZE_LIMIT = 104; // px, limiting estimated height by this value because all texts are collapsed by default
const ESTIMATED_ITEM_SIZE_OFFSET = 38; // px, a rough height to add to an estimated text height to get full NoteItem height
const NOTES_PAGE_SIZE = 25; // load notes in these portions, as user scrolls down
const SCROLL_POSITION_THRESHOLD = 100; // px, load next portion of notes when notes list is this close to its bottom

const noteKeyGetter = (index: number, data: ListData) => data.notes[index].id;

interface Props {
  allowedUserIds?: User["id"][];
  createLoading: boolean;
  deleteLoadingIds: Note["id"][];
  notes: Note[];
  onChangeNote?: (note: Note) => void;
  onCreateNote?: (noteText: string) => void;
  onDeleteNote?: (note: Note) => void;
  onNotesChange?: (notesChanged: boolean) => void;
  teams: Team[];
  uneditable?: boolean;
  unreadable?: boolean;
  updateLoadingIds: Note["id"][];
  users: User[];
}

const EditNotesPanel: React.FC<Props> = ({
  createLoading,
  deleteLoadingIds,
  notes,
  onChangeNote,
  onCreateNote,
  onDeleteNote,
  onNotesChange,
  teams,
  uneditable,
  unreadable,
  updateLoadingIds,
  users,
}) => {
  const intl = useIntl();
  const [isAddingNote, startAddingNote, stopAddingNote] = useBoolean();
  const [notesEditing, setNotesEditing] = useState<boolean>(false);

  const notesAnalytics = useExtendedAnalytics("Notes", useAnalytics());
  useModalVisibilityAnalyticEffect("Add Note", isAddingNote, notesAnalytics);

  const handleAddNote = useCallback(() => {
    notesAnalytics?.clicked(["Add Note"]);
    startAddingNote();
  }, [notesAnalytics, startAddingNote]);

  const handleCancelNote = useCallback(() => {
    notesAnalytics?.cancelled(["Add Note", "Cancel"]);
    stopAddingNote();
  }, [notesAnalytics, stopAddingNote]);

  const handleDeleteNote = useCallback(
    (note: Note) => {
      notesAnalytics?.clicked(["Delete Note"], { note });
      onDeleteNote?.(note);
    },
    [notesAnalytics, onDeleteNote]
  );

  const handleSaveNote = useCallback(
    (noteText: string) => {
      notesAnalytics?.clicked(["Save Note"], { noteText });
      onCreateNote?.(noteText);
    },
    [onCreateNote, notesAnalytics]
  );

  const hiddenRowRef = useRef<HTMLDivElement>(null);

  const heights = useRef<Record<Note["id"], number>>({});
  const getHeight = useCallback(
    (index: number) => {
      if (!heights.current[notes[index].id]) {
        // If height is unknown, let's try estimating it or take the DEFAULT_ITEM_SIZE
        // Estimation is not very precise here, but it still gives good overall results.
        let height = DEFAULT_ITEM_SIZE;
        if (hiddenRowRef.current) {
          hiddenRowRef.current.textContent = notes[index].note;
          height = Math.min(
            ESTIMATED_ITEM_SIZE_LIMIT,
            ESTIMATED_ITEM_SIZE_OFFSET +
              parseFloat(
                window
                  .getComputedStyle(hiddenRowRef.current as Element)
                  .getPropertyValue("height")
                  .replace(/\D+$/g, "")
              )
          );
        }
        heights.current[notes[index].id] = height;
      }

      return heights.current[notes[index].id];
    },
    [notes]
  );

  const listRef = useRef<VariableSizeList>(null);

  useEffect(() => {
    onNotesChange?.(isAddingNote || notesEditing);
  }, [isAddingNote, notesEditing, onNotesChange]);

  const [maxVisibleNoteIndex, setMaxVisibleNoteIndex] = useState(
    Math.min(NOTES_PAGE_SIZE, notes.length - 1)
  );
  const partialNotes = useMemo(
    () => notes.slice(0, maxVisibleNoteIndex + 1),
    [notes, maxVisibleNoteIndex]
  );

  const listHeight = useRef(0);
  const scrolledToTheBottom = useRef(false);
  const handleScroll = useCallback(({ scrollDirection, scrollOffset }: ListOnScrollProps) => {
    const totalItemsHeight = Object.values(heights.current).reduce((sum, value) => sum + value, 0);
    scrolledToTheBottom.current =
      scrollDirection === "forward" &&
      scrollOffset + listHeight.current >= totalItemsHeight - SCROLL_POSITION_THRESHOLD;
  }, []);

  const renderedItemsPropsRef = useRef<ListOnItemsRenderedProps | undefined>();
  const handleItemsRendered = useCallback(
    (renderedItemsProps: ListOnItemsRenderedProps) => {
      renderedItemsPropsRef.current = renderedItemsProps;
      if (
        renderedItemsProps.overscanStopIndex === maxVisibleNoteIndex &&
        scrolledToTheBottom.current
      ) {
        if (notes.length - 1 > maxVisibleNoteIndex) {
          setMaxVisibleNoteIndex((index) => Math.min(index + NOTES_PAGE_SIZE, notes.length - 1));
        }
      }
    },
    [notes.length, maxVisibleNoteIndex]
  );

  const handleHeightChange = useCallback((note: Note, index: number, height: number) => {
    const { visibleStartIndex = 0, visibleStopIndex = 0 } = renderedItemsPropsRef.current ?? {};
    if (
      heights.current[note.id] !== height &&
      // only updated height for visible items, because invisible items height is calculated incorrectly
      index >= visibleStartIndex &&
      index <= visibleStopIndex
    ) {
      heights.current = { ...heights.current, [note.id]: height };
      listRef.current?.resetAfterIndex(index, true);
    }
  }, []);

  useEffect(() => {
    // re-render the list when notes are changed
    listRef.current?.resetAfterIndex(0, true);
    // if there were less than 1 page of notes, update maxVisibleNoteIndex to include all
    // notes from the 1st page
    setMaxVisibleNoteIndex((max) =>
      max < NOTES_PAGE_SIZE && notes.length - 1 > max
        ? Math.min(NOTES_PAGE_SIZE, notes.length - 1)
        : max
    );
  }, [notes]);

  return (
    <TabLayout
      action={
        <AddOrCreateButton
          disabled={unreadable || uneditable}
          label={intl.formatMessage({
            id: "recordPane.noteList.addButton",
            defaultMessage: "Add",
            description: "Title of a button to add a note to a company",
          })}
          loading={createLoading}
          onClick={handleAddNote}
          tooltip={
            unreadable || uneditable
              ? intl.formatMessage({
                  id: "recordPane.noteList.addButton.uneditable",
                  defaultMessage:
                    "You do not have permission to use this feature. Please contact your admin to request a change.",
                  description: "Tooltip for an Add note Button when user has no edit rights",
                })
              : undefined
          }
        />
      }
      title={intl.formatMessage(
        {
          id: "recordPane.noteList.title",
          defaultMessage: "Notes {count}",
          description: "Title of a Notes section on a record pane",
        },
        { count: <span className={styles.total}>({notes.length})</span> }
      )}
    >
      {isAddingNote && (
        <EditNoteForm
          isSaving={createLoading}
          onCancel={handleCancelNote}
          onSave={handleSaveNote}
        />
      )}

      {notes.length > 0 && !unreadable ? (
        <div className={styles.list}>
          <AutoSizer>
            {({ height, width }) => {
              listHeight.current = height;
              return (
                <VariableSizeList<ListData>
                  height={height}
                  itemCount={partialNotes.length}
                  itemData={{
                    canEdit: !uneditable,
                    deleteLoadingIds,
                    notes: partialNotes,
                    onChange: onChangeNote,
                    onDelete: handleDeleteNote,
                    onHeightChange: handleHeightChange,
                    onNotesChange: setNotesEditing,
                    teams,
                    updateLoadingIds,
                    users,
                  }}
                  itemKey={noteKeyGetter}
                  itemSize={getHeight}
                  onItemsRendered={handleItemsRendered}
                  onScroll={handleScroll}
                  overscanCount={10}
                  ref={listRef}
                  width={width}
                >
                  {ListItem}
                </VariableSizeList>
              );
            }}
          </AutoSizer>
          <div className={styles.blankDiv} ref={hiddenRowRef}></div>
        </div>
      ) : (
        <NoData
          imageSrc={noNotes}
          text={
            unreadable
              ? intl.formatMessage({
                  id: "recordPane.noteList.unreadable",
                  defaultMessage: "Notes are hidden",
                  description: "Text of the 'PHI-hidden' state in Notes section in a Edit pane",
                })
              : intl.formatMessage({
                  id: "recordPane.noteList.noData",
                  defaultMessage: "Add a note to populate this list",
                  description: "Text of the 'No Data' state in Notes section in a Edit pane",
                })
          }
        />
      )}
    </TabLayout>
  );
};

const mapStateToProps = (state: RootState) => ({
  teams: getAllTeamsOfEntireOrganization(state),
  users: getUsers(state),
});

export default connect(mapStateToProps)(EditNotesPanel);
